import { Link } from "@tanstack/react-router";
import { ChevronDown, FileQuestion, Flag } from "lucide-react";
import { useCallback, useState } from "react";
import {
  BandLegend,
  GroupHeading,
  MetaText,
  ScoreBar,
  ScoreValue,
  StatusPill,
} from "@/components/plan-audit/AuditPrimitives";
import { SectionDrawer } from "@/components/plan-audit/SectionDrawer";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type RunDetail, type RunSection, useRun } from "@/hooks/use-plan-audit";
import { SCORE_GROUPS } from "@/lib/enums";
import { formatDate, formatDuration, formatFields } from "@/lib/plan-audit-format";

/**
 * The scorecard — 25 sections grouped by Why / Who / What / Where / When /
 * Governance, each openable for the full finding.
 *
 * Two columns of three groups, which is how the design lays it out and also how
 * it stays readable: the six groups are uneven (What has nine sections, Where
 * two), so a single column would put a wall of "What" between the reader and
 * everything else.
 */
export function ScorecardView({ reference }: { reference: string }) {
  const { data: run, isLoading, isError, error, refetch } = useRun(reference);
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((name: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  if (isLoading) return <ScorecardSkeleton />;
  if (isError) return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  if (!run) return <ErrorState message="Run not found" />;

  if (run.sectionDetailMissing) {
    return (
      <>
        <RunHeader run={run} />
        <div className="pa-card mt-4">
          <EmptyState
            icon={FileQuestion}
            title="No section scores stored for this run"
            description={
              run.status === "complete"
                ? "This run recorded an overall score but not the per-section breakdown. Re-auditing this plan version would produce a full scorecard."
                : "A scorecard appears once the run completes."
            }
            action={
              <Button asChild variant="outline">
                <Link to="/solutions/$slug" params={{ slug: run.solution.slug }}>
                  Back to {run.solution.name}
                </Link>
              </Button>
            }
          />
        </div>
      </>
    );
  }

  const left = SCORE_GROUPS.slice(0, 3);
  const right = SCORE_GROUPS.slice(3);

  return (
    <>
      <RunHeader run={run} />

      <div className="mt-4 mb-3 flex flex-wrap items-center justify-between gap-3">
        <BandLegend />
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen(new Set(run.sections.map((s) => s.name)))}
          >
            Open all
          </Button>
          <Button variant="outline" size="sm" onClick={() => setOpen(new Set())}>
            Close all
          </Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="flex flex-col gap-4">
          {left.map((group) => (
            <GroupCard key={group} run={run} group={group} open={open} onToggle={toggle} />
          ))}
        </div>
        <div className="flex flex-col gap-4">
          {right.map((group) => (
            <GroupCard key={group} run={run} group={group} open={open} onToggle={toggle} />
          ))}
        </div>
      </div>
    </>
  );
}

function RunHeader({ run }: { run: RunDetail }) {
  const flagCount = run.flags.length;
  return (
    <div className="pa-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-bold text-[var(--brand-primary)]">
              {run.solution.name}
            </h2>
            <StatusPill status={run.status} />
          </div>
          <MetaText>
            {run.planVersion.label} · run {run.reference} · scored against Guide {run.guide.label} ·{" "}
            {formatDate(run.startedAt)} · {formatDuration(run.durationSeconds)}
          </MetaText>
          {run.executedByEmail.length > 0 && (
            <div>
              {/* Recorded so usage stays attributable: a run bills the operator's
                  own Claude subscription, not a shared Nymbl key. */}
              <MetaText>Executed on {run.executedByEmail}</MetaText>
            </div>
          )}
        </div>

        <div className="flex items-center gap-5">
          <div className="text-right">
            <div className="pa-eyebrow">Overall</div>
            <ScoreValue score={run.overallScore} size="lg" />
          </div>
          <Button asChild variant="outline">
            <Link to="/runs/$reference/flags" params={{ reference: run.reference }}>
              <Flag aria-hidden="true" />
              {flagCount} flag{flagCount === 1 ? "" : "s"}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function GroupCard({
  run,
  group,
  open,
  onToggle,
}: {
  run: RunDetail;
  group: (typeof SCORE_GROUPS)[number];
  open: Set<string>;
  onToggle: (name: string) => void;
}) {
  const summary = run.groups.find((g) => g.group === group);
  const sections = run.sections.filter((s) => s.group === group);
  if (sections.length === 0) return null;

  return (
    <section className="pa-card">
      <GroupHeading
        group={group}
        mean={summary?.mean ?? null}
        sectionCount={summary?.sectionCount ?? sections.length}
      />
      {sections.map((section) => (
        <SectionRow
          key={section.id}
          section={section}
          open={open.has(section.name)}
          onToggle={() => onToggle(section.name)}
        />
      ))}
    </section>
  );
}

function SectionRow({
  section,
  open,
  onToggle,
}: {
  section: RunSection;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        className="pa-row"
        data-open={open}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="min-w-0">
          <span
            className="block truncate text-[13px] text-[var(--fg-1)]"
            style={{ fontWeight: open ? 600 : 400 }}
          >
            {section.name}
          </span>
          <MetaText>
            {section.absent ? (
              // Absent is not a low score. Saying so on the row is what keeps the
              // gap visible instead of reading as "written badly".
              <span style={{ color: "var(--danger-fg)" }}>absent from the plan</span>
            ) : (
              `${formatFields(section.fieldsPresent, section.fieldsRequired)} of what the Guide asks`
            )}
          </MetaText>
        </span>
        <span className="pa-row-bar">
          <ScoreBar score={section.score} />
        </span>
        <span className="text-right">
          <ScoreValue score={section.score} size="sm" />
        </span>
        <ChevronDown
          aria-hidden="true"
          size={16}
          className="text-[var(--fg-3)] transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      {open && <SectionDrawer section={section} />}
    </div>
  );
}

function ScorecardSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-28 w-full rounded-xl" />
      <div className="grid gap-4 xl:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-64 w-full rounded-xl" />
        ))}
      </div>
    </div>
  );
}
